import { useCallback, useMemo } from 'react';
import { MemberType, Team, UserType } from 'api/models';
import { Project } from 'api/project';
import { Option } from 'components/atoms/select';
import {
  CompanyOption,
  Extra,
  UserOption,
} from 'components/molecules/squad-picker';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useRolesQuery } from 'queries/roles';
import { useUsersForProject } from './project-users';

interface Props {
  project: Project;
  placeholder: Team;
}
interface Result {
  usersInSameRole: Option<Extra>[];
  usersInProject: Option<Extra>[];
  partnersInProject: Option<Extra>[];
  usersNotInProject: Option<Extra>[];
}
export const useOptionsForPlaceholderValue = ({
  project,
  placeholder,
}: Props): Result => {
  const {
    user: { userType: loggedInUserType, company: loggedInUserCompany },
  } = useUser();
  const { users: projectUsers } = useUsersForProject({ project });
  const usersInSameRole = useMemo(() => {
    const inProjectUsersInSameRole =
      placeholder.roleId == null
        ? []
        : projectUsers.inProjectUsers.filter(
            (user) =>
              user.role?.roleId === placeholder.roleId &&
              (placeholder.teamType === MemberType.Native
                ? user.company.companyId !== project.customer.companyId
                : user.company.companyId === project.customer.companyId),
          );

    if (loggedInUserType === UserType.Partner) {
      const notInProjectUsersInSameRole =
        placeholder.roleId == null
          ? []
          : projectUsers.notInProjectUsers.filter(
              (user) =>
                user.role?.roleId === placeholder.roleId &&
                loggedInUserCompany.companyId === user.company.companyId,
            );

      return inProjectUsersInSameRole
        .filter(
          (user) => user.company.companyId === loggedInUserCompany.companyId,
        )
        .map((user) => UserOption(user, true))
        .concat(
          notInProjectUsersInSameRole.map((user) => UserOption(user, false)),
        );
    }

    return inProjectUsersInSameRole.map((user) => UserOption(user, true));
  }, [
    loggedInUserCompany.companyId,
    loggedInUserType,
    placeholder.roleId,
    placeholder.teamType,
    project.customer.companyId,
    projectUsers.inProjectUsers,
    projectUsers.notInProjectUsers,
  ]);
  const usersInProject = useMemo(
    () =>
      projectUsers.inProjectUsers
        .filter(
          (user) =>
            (placeholder.teamType === MemberType.Native
              ? user.company.companyId !== project.customer.companyId
              : user.company.companyId === project.customer.companyId) &&
            !usersInSameRole.find((x) => x.extra?.id === user.userId),
        )
        .map((user) => UserOption(user)),
    [
      placeholder.teamType,
      project.customer.companyId,
      projectUsers.inProjectUsers,
      usersInSameRole,
    ],
  );
  const partnersInProject = useMemo(
    () =>
      loggedInUserType === UserType.Native &&
      placeholder.teamType === MemberType.Native
        ? project.partners.map(CompanyOption)
        : [],
    [loggedInUserType, placeholder.teamType, project.partners],
  );
  const usersNotInProject = useMemo(
    () =>
      projectUsers.notInProjectUsers
        .filter(
          (user) =>
            (placeholder.teamType === MemberType.Native
              ? user.company.companyId !== project.customer.companyId
              : user.company.companyId === project.customer.companyId) &&
            !usersInSameRole.find((x) => x.extra?.id === user.userId),
        )
        .map((user) => UserOption(user)),
    [
      placeholder.teamType,
      project.customer.companyId,
      projectUsers.notInProjectUsers,
      usersInSameRole,
    ],
  );

  return {
    usersInSameRole,
    usersInProject,
    partnersInProject,
    usersNotInProject,
  };
};

export const useOptionsCombinedForPlaceholderValue = ({
  project,
  placeholder,
}: Props): {
  options: {
    id: string;
    label: string;
    options: Option<Extra>[];
    description?: string;
  }[];
} => {
  const {
    user: { userType: loggedInUserType, company: loggedInUserCompany },
  } = useUser();
  const {
    usersInProject,
    usersInSameRole,
    usersNotInProject,
    partnersInProject,
  } = useOptionsForPlaceholderValue({ placeholder, project });
  const { data: roles } = useRolesQuery();
  const getRoleName = useCallback(
    (roleId?: number) =>
      roles?.find(({ roleId: id }) => id === roleId)?.roleName ?? '',
    [roles],
  );
  const translate = useTranslate();
  const labelForUserInSameRole = useMemo(
    () =>
      loggedInUserType === UserType.Partner
        ? translate(
            'projects.placeholder_resolutions.items.same_role.partner_user',
            {
              companyName: loggedInUserCompany.companyName,
              roleName: getRoleName(placeholder.roleId),
            },
          )
        : translate('placeholder.replace.users.options.matching.roles', {
            roleName: getRoleName(placeholder.roleId),
          }),
    [
      getRoleName,
      loggedInUserCompany.companyName,
      loggedInUserType,
      placeholder.roleId,
      translate,
    ],
  );

  const options = useMemo(() => {
    return [
      {
        id: 'userInSameRole',
        label: labelForUserInSameRole,
        options: usersInSameRole,
      },
      {
        id: 'usersInProject',
        label: translate('user_in_project'),
        options: usersInProject,
      },
      {
        id: 'partners',
        label: translate('partners'),
        description: translate(
          'projects.placeholder_resolutions.items.partner.description',
        ),
        options: partnersInProject,
      },
      {
        id: 'usersNotInProject',
        label: translate('not_in_project'),
        options: usersNotInProject,
      },
    ];
  }, [
    labelForUserInSameRole,
    partnersInProject,
    translate,
    usersInProject,
    usersInSameRole,
    usersNotInProject,
  ]);

  return {
    options,
  };
};
